import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { useCreateProject } from '../hooks/useProjectMutations'
import type { components } from '../lib/api-types'

type IngestionSource = components['schemas']['IngestionSource']
const SOURCES: IngestionSource[] = ['manifest_yaml', 'structurizr_dsl', 'terraform', 'manual']

/** Manual enrolment form. Submitting opens a confirm-with-reason dialog (R6)
 *  before the project is created — the reason is recorded in the audit log. */
export function EnrollProjectForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const create = useCreateProject()
  const [name, setName] = useState('')
  const [repoUrl, setRepoUrl] = useState('')
  const [source, setSource] = useState<IngestionSource>('manifest_yaml')
  const [confirming, setConfirming] = useState(false)

  const valid = name.trim() !== '' && repoUrl.trim() !== ''

  return (
    <div className="rounded border border-neutral-200 p-4 dark:border-neutral-800">
      <div className="grid gap-3">
        <label className="text-sm">
          {t('directory.form.name')}
          <input value={name} onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded border border-neutral-300 px-2 py-1 text-sm dark:bg-neutral-800" />
        </label>
        <label className="text-sm">
          {t('directory.form.repo')}
          <input value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)}
            className="mt-1 w-full rounded border border-neutral-300 px-2 py-1 text-sm dark:bg-neutral-800" />
        </label>
        <label className="text-sm">
          {t('directory.form.source')}
          <select value={source} onChange={(e) => setSource(e.target.value as IngestionSource)}
            className="mt-1 w-full rounded border border-neutral-300 px-2 py-1 text-sm dark:bg-neutral-800">
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <button type="button" disabled={!valid} onClick={() => setConfirming(true)}
          className="rounded bg-neutral-900 px-3 py-2 text-sm text-white disabled:opacity-40 dark:bg-neutral-100 dark:text-neutral-900">
          {t('directory.form.submit')}
        </button>
        {create.isError && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {(create.error as { status?: number }).status === 409
              ? t('directory.form.duplicate')
              : t('directory.form.error')}
          </p>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        title={t('directory.confirmTitle')}
        reasonLabel={t('directory.confirmReason')}
        confirmLabel={t('directory.confirm')}
        cancelLabel={t('directory.cancel')}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          // Close the confirm dialog on both outcomes; the form surfaces any
          // error inline so the user can fix the repo URL and retry.
          create.mutate(
            { name: name.trim(), repoUrl: repoUrl.trim(), ingestionSource: source, defaultBranch: 'main' },
            { onSuccess: () => { setConfirming(false); onClose() }, onError: () => setConfirming(false) },
          )
        }}
      />
    </div>
  )
}
